import React from 'react';
import { format, parseISO } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { 
  Tooltip as UITooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { InfoIcon } from 'lucide-react';

interface SymptomTrendsChartProps {
  data: any[];
  selectedSymptoms: string[];
}

export const SymptomTrendsChart: React.FC<SymptomTrendsChartProps> = ({ data, selectedSymptoms }) => {
  // Transform data for recharts
  const chartData = React.useMemo(() => {
    const symptomsByDate: { [key: string]: any } = {};
    
    // Group by date
    data.forEach(entry => {
      const date = format(parseISO(entry.logged_at), 'yyyy-MM-dd');
      if (!symptomsByDate[date]) {
        symptomsByDate[date] = { date };
      }
      
      // Mark each selected symptom as present if it exists in the entry
      entry.symptoms.forEach((symptom: string) => {
        if (selectedSymptoms.includes(symptom)) {
          symptomsByDate[date][symptom] = (symptomsByDate[date][symptom] || 0) + 1;
        }
      });
    });
    
    // Convert to array and ensure all symptoms have a value for each date
    return Object.values(symptomsByDate).map(dateEntry => {
      selectedSymptoms.forEach(symptom => {
        if (dateEntry[symptom] === undefined) {
          dateEntry[symptom] = 0;
        }
      });
      return dateEntry;
    }).sort((a: any, b: any) => a.date.localeCompare(b.date));
  }, [data, selectedSymptoms]);
  
  // Define colors for each symptom
  const symptomColors = {
    'Fatigue': '#FFA07A', // Light Salmon
    'Headache': '#FF6B6B', // Light Red
    'Bloating': '#FFD700', // Gold
    'Cramps': '#FF4500', // Orange Red
    'Mood Swings': '#9370DB', // Medium Purple
    'Insomnia': '#4682B4', // Steel Blue
    'Low Energy': '#7B68EE', // Medium Slate Blue
    'Cravings': '#32CD32', // Lime Green
    // Add more symptoms with their colors as needed
  };
  
  // Configuration for chart
  const chartConfig = {
    ...selectedSymptoms.reduce((acc, symptom) => {
      // @ts-ignore
      acc[symptom] = { color: symptomColors[symptom] || '#CCCCCC' };
      return acc;
    }, {})
  };
  
  // Custom tooltip formatter
  const customTooltipFormatter = (value: number, name: string) => {
    return [
      `${value > 0 ? value : 'Not reported'}`, 
      `${name}`
    ];
  };
  
  return (
    <div className="w-full h-[400px]">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-hormony-charcoal">Symptom Trends Over Time</h3>
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <div className="cursor-help">
                <InfoIcon size={16} className="text-hormony-charcoal/60" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>This chart shows when each symptom occurred over time. Higher points indicate days when symptoms were reported more frequently.</p>
              <p className="mt-1 text-xs text-hormony-charcoal/70">Hover over data points for more details.</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
      
      <ChartContainer config={chartConfig}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="date" 
              tickFormatter={(value) => format(new Date(value), 'MMM d')}
              angle={-30}
              textAnchor="end"
              height={60}
            />
            <YAxis />
            <ChartTooltip 
              content={<ChartTooltipContent />} 
              formatter={customTooltipFormatter}
            />
            <Legend 
              layout="horizontal" 
              verticalAlign="top" 
              align="center"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            {selectedSymptoms.map((symptom, index) => (
              <Line 
                key={symptom}
                type="monotone" 
                dataKey={symptom} 
                stroke={symptomColors[symptom as keyof typeof symptomColors] || `#${Math.floor(Math.random()*16777215).toString(16)}`}
                activeDot={{ r: 8 }}
                strokeWidth={2}
                dot={{ r: 3 }}
                name={symptom}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};
